// Practice topics for the Sanskrit games, the same idea as the Prakrit topic filter: chips grouped as लकार, विभक्ति,
// वचन, पुरुष, सन्धि and समास. Several chips of one group mean "any of these", chips of different groups mean "all of
// these". `?topic=lat,eka` in a practice URL carries them; each game uses only the groups it understands
// (GAME_TOPICS), and a chosen topic overrides the level's usual range.

import type { Bi, Lakara, Purusha, Vacana, Vibhakti } from '@/sanskrit/lib/sanskrit/types'
import type { SamasaType } from '@/sanskrit/lib/sanskrit/samasa'
import type { GameId, SandhiKind, TopicScope } from '@/sanskrit/lib/practice/generators'

import { LAKARA_LABEL, PURUSHA_LABEL, VACANA_LABEL, VIBHAKTI_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { SAMASA_TYPES } from '@/sanskrit/lib/sanskrit/samasa'

export type TopicGroupId = 'lakara' | 'vibhakti' | 'vacana' | 'purusha' | 'sandhi' | 'samasa'

export interface PracticeTopic {
    id: string
    group: TopicGroupId
    /** The Sanskrit name shown on the chip. */
    sa: string
    label: Bi
}

export const TOPIC_GROUPS: { id: TopicGroupId, sa: string, title: Bi }[] = [
    { id: 'lakara', sa: 'लकारः', title: { hi: 'लकार (काल)', en: 'Tense (lakāra)' } },
    { id: 'vibhakti', sa: 'विभक्तिः', title: { hi: 'विभक्ति', en: 'Case (vibhakti)' } },
    { id: 'vacana', sa: 'वचनम्', title: { hi: 'वचन', en: 'Number (vacana)' } },
    { id: 'purusha', sa: 'पुरुषः', title: { hi: 'पुरुष', en: 'Person (puruṣa)' } },
    { id: 'sandhi', sa: 'सन्धिः', title: { hi: 'सन्धि', en: 'Sandhi' } },
    { id: 'samasa', sa: 'समासः', title: { hi: 'समास', en: 'Compound (samāsa)' } }
]

const LAKARAS: Lakara[] = ['lat', 'lan', 'lrt', 'lot', 'vidhilin']
const VIBS: Vibhakti[] = [1, 2, 3, 4, 5, 6, 7, 8]
const VACANAS: Vacana[] = ['eka', 'dvi', 'bahu']
const PURUSHAS: Purusha[] = ['prathama', 'madhyama', 'uttama']

const SANDHI: { id: SandhiKind, sa: string, label: Bi }[] = [
    { id: 'savarna-dirgha', sa: 'दीर्घ', label: { hi: 'दीर्घ सन्धि', en: 'Long vowel (dīrgha)' } },
    { id: 'guna', sa: 'गुण', label: { hi: 'गुण सन्धि', en: 'Guṇa' } },
    { id: 'vrddhi', sa: 'वृद्धि', label: { hi: 'वृद्धि सन्धि', en: 'Vṛddhi' } },
    { id: 'yan', sa: 'यण्', label: { hi: 'यण् सन्धि', en: 'Yaṇ' } },
    { id: 'ayadi', sa: 'अयादि', label: { hi: 'अयादि सन्धि', en: 'Ayādi' } },
    { id: 'purvarupa', sa: 'पूर्वरूप', label: { hi: 'पूर्वरूप सन्धि', en: 'Pūrvarūpa' } },
    { id: 'visarga', sa: 'विसर्ग', label: { hi: 'विसर्ग सन्धि', en: 'Visarga' } },
    { id: 'vyanjana', sa: 'व्यञ्जन', label: { hi: 'व्यञ्जन सन्धि', en: 'Consonant' } }
]

export const TOPICS: PracticeTopic[] = [
    ...LAKARAS.map(l => ({ id: l, group: 'lakara' as const, sa: LAKARA_LABEL[l].sa, label: { hi: LAKARA_LABEL[l].hi, en: LAKARA_LABEL[l].en } })),
    ...VIBS.map(v => ({ id: `v${v}`, group: 'vibhakti' as const, sa: VIBHAKTI_LABEL[v].sa, label: { hi: VIBHAKTI_LABEL[v].hi, en: VIBHAKTI_LABEL[v].en } })),
    ...VACANAS.map(v => ({ id: v, group: 'vacana' as const, sa: VACANA_LABEL[v].sa, label: { hi: VACANA_LABEL[v].hi, en: VACANA_LABEL[v].en } })),
    ...PURUSHAS.map(p => ({ id: p, group: 'purusha' as const, sa: PURUSHA_LABEL[p].sa, label: { hi: PURUSHA_LABEL[p].hi, en: PURUSHA_LABEL[p].en } })),
    ...SANDHI.map(s => ({ id: s.id, group: 'sandhi' as const, sa: s.sa, label: s.label })),
    ...(Object.keys(SAMASA_TYPES) as SamasaType[]).map(k => ({ id: k, group: 'samasa' as const, sa: SAMASA_TYPES[k].sa, label: SAMASA_TYPES[k].name }))
]

export const TOPIC_BY_ID: Record<string, PracticeTopic> = Object.fromEntries(TOPICS.map(t => [t.id, t]))

const ids = (group: TopicGroupId, only?: string[]): string[] => TOPICS.filter(t => t.group === group && (!only || only.includes(t.id))).map(t => t.id)

/** The topics each game understands. */
export const GAME_TOPICS: Record<GameId, string[]> = {
    'dhatu-challenge': [...ids('lakara'), ...ids('vacana'), ...ids('purusha')],
    'lakara-pehchan': [...ids('lakara'), ...ids('vacana'), ...ids('purusha')],
    'shabdarupa-sadhana': [...ids('vibhakti'), ...ids('vacana')],
    'vibhakti-chayan': [...ids('vibhakti'), ...ids('vacana')],
    'sandhi-nirmata': ids('sandhi'),
    'sandhi-viccheda': ids('sandhi'),
    'samasa-pehchan': ids('samasa'),
    'vakya-nirmata': [...ids('lakara', ['lat', 'lan', 'lrt']), ...ids('vacana'), ...ids('purusha')],
    shravan: [...ids('lakara'), ...ids('vacana'), ...ids('purusha')],
    'akshara-nirman': [...ids('vibhakti'), ...ids('vacana')],
    dvivacana: [...ids('lakara'), ...ids('vibhakti'), ...ids('purusha')],
    'katha-krama': []
}

/** The groups the topic filter on the practice hub shows. */
export const gameGroups = (game: GameId): TopicGroupId[] => TOPIC_GROUPS.map(g => g.id).filter(g => GAME_TOPICS[game].some(id => TOPIC_BY_ID[id]?.group === g))

/** Topic ids from `?topic=`, known ones only, in the order of TOPICS. */
export const parseTopics = (raw: string | string[] | undefined): string[] => {
    const text = Array.isArray(raw) ? raw.join(',') : raw ?? ''
    const wanted = new Set(text.split(',').map(s => s.trim()).filter(Boolean))

    return TOPICS.filter(t => wanted.has(t.id)).map(t => t.id)
}

/** The chosen topics this game understands. */
export const topicsForGame = (game: GameId, topics: string[]): string[] => topics.filter(id => GAME_TOPICS[game].includes(id))

/** True when the learner chose topics and this game understands none of them. */
export const gameIsOff = (game: GameId, topics: string[]): boolean => topics.length > 0 && topicsForGame(game, topics).length === 0

export const topicQuery = (topics: string[]): string => topics.join(',')

export const practiceHref = (game: GameId, level: number, topics: string[] = [], set?: number): string => {
    const params = new URLSearchParams()

    params.set('level', String(level))
    if (topics.length > 0) params.set('topic', topicQuery(topics))
    if (set) params.set('set', String(set))

    return `/sanskrit/practice/${game}?${params.toString()}`
}

/** What the generators get from the chosen topics. */
export const scopeOf = (game: GameId, topics: string[]): TopicScope => {
    const chosen = topicsForGame(game, topics).map(id => TOPIC_BY_ID[id])
    const of = (group: TopicGroupId) => chosen.filter(t => t.group === group).map(t => t.id)
    const lakaras = of('lakara') as Lakara[]
    const vibhaktis = of('vibhakti').map(id => Number(id.slice(1)) as Vibhakti)
    const vacanas = of('vacana') as Vacana[]
    const purushas = of('purusha') as Purusha[]
    const sandhi = of('sandhi') as SandhiKind[]
    const samasa = of('samasa') as SamasaType[]

    return {
        ...(lakaras.length > 0 && { lakaras }),
        ...(vibhaktis.length > 0 && { vibhaktis }),
        ...(vacanas.length > 0 && { vacanas }),
        ...(purushas.length > 0 && { purushas }),
        ...(sandhi.length > 0 && { sandhi }),
        ...(samasa.length > 0 && { samasa })
    }
}
